// @flow
import * as React from 'react';
import {useState} from 'react';
import PropTypes from 'prop-types';

import type {Store, PaymentMethod, Currency} from '../../../../../entities';
import {Link} from 'react-router-dom';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  makeStyles,
  Typography,
  Snackbar,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../assets/styles/colors';
import {useForm} from 'react-hook-form';
import {useEffect} from 'react';
import {ArrowBack} from '@material-ui/icons';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';

import CustomExitPrompt from '../../../../../elements/CustomExitPrompt/CustomExitPrompt';
import NewFeatureBadge from '../../../../../elements/NewFeatureBadge/NewFeatureBadge';
import {useIsFeatureInTier} from '../../../../../utils/component';

import type {
  PaymentMethodAction,
  MercadoPagoCheckoutProRequest,
} from '../../duck';
import {MERCADO_PAGO_SUPPORTED_CURRENCY_CODES} from '../../utils/paymentData';

type Props = {|
  isFetching?: boolean,
  isSaving?: boolean,
  isDeleting?: boolean,
  addSuccess?: boolean,
  updateSuccess?: boolean,
  deleteSuccess?: boolean,
  error?: string,
  // addPaymentMethod: ({paymentMethodRequest: PaymentMethod}) => void,
  updateMercadoPagoCheckoutPro: (payload: PaymentMethodAction) => void,
  paymentMethod: PaymentMethod,
  paymentMethods: PaymentMethod[],
  getPaymentMethod: ({id: string}) => void,
  resetState: () => void,
  store: Store,
  currency?: Currency,
  disableActivation?: boolean,
|};

export const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
    },
    // padding: theme.spacing(0, 3),
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
  },
  subRouteTitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    fontWeight: 600,
  },
  divider: {
    background: colors.grey200,
    width: '100%',
  },
  checkbox: {
    marginTop: theme.spacing(2),
  },
  checkboxLabel: {
    color: colors.grey700,
    fontSize: '0.875rem',
    // marginTop: theme.spacing(3),
  },
  descriptionText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  contactButton: {},
  row: {
    display: 'flex',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
    },
  },
  textField: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      '&:not(:last-child)': {
        marginRight: theme.spacing(2),
      },
    },
  },
  greyText: {
    color: colors.grey600,
    fontSize: '0.75rem',
  },
  messageSpace: {
    margin: theme.spacing(2, 0),
    alignSelf: 'stretch',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  saveButton: {
    margin: theme.spacing(3, 0),
    [theme.breakpoints.up('sm')]: {
      width: 200,
    },
    [theme.breakpoints.down('xs')]: {
      zIndex: 1,
      position: 'fixed',
      left: 0,
      bottom: 64,
      margin: theme.spacing(2),
      height: 48,
      width: `calc(100% - ${theme.spacing(4)})`,
      fontSize: '1.125rem',
    },
  },
  alert: {
    width: '100%',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
}));

export default function MercadoPagoConfig(props: Props) {
  const {
    // isFetching,
    isSaving,
    // isDeleting,
    // addSuccess,
    // updateSuccess,
    // error,
    // addPaymentMethod,
    updateMercadoPagoCheckoutPro,
    paymentMethod,
    // paymentMethods,
    // getPaymentMethod,
    // deleteSuccess,
    // store,
    currency,
    disableActivation,
  } = props;

  const classes = useStyles();

  const [disabledButton, setDisabledButton] = useState(false);
  const [showWarningCurrency, setShowWarningCurrency] = useState(false);
  const hasMercadopagoCheckoutProIntegrationFeature = useIsFeatureInTier(
    'MERCADOPAGO_CHECKOUT_PRO_INTEGRATION'
  );

  const [enabled, setEnabled] = useState(
    paymentMethod ? paymentMethod.enabled : false
  );

  // useEffect(() => {
  //   if (paymentMethods) {
  //     const mercadoMethod = paymentMethods.find(
  //       method => method.displayName === 'Mercado Pago'
  //     );
  //     if (mercadoMethod && mercadoMethod.id) {
  //       getPaymentMethod({id: mercadoMethod.id});
  //     }
  //   }
  // }, [paymentMethods, getPaymentMethod]);

  useEffect(() => {
    if (paymentMethod && paymentMethod.enabled)
      setEnabled(paymentMethod.enabled);
  }, [paymentMethod]);

  const {register, handleSubmit, errors, formState} = useForm({
    mode: 'onChange',
  });
  const {isDirty} = formState;

  const handleToggleEnabled = event => {
    if (event) {
      setEnabled(event.target.checked);
      if (!hasMercadopagoCheckoutProIntegrationFeature) return;
      if (!event.target.checked) {
        const paymentMethodRequest = {
          enabled: false,
        };
        updateMercadoPagoCheckoutPro({
          mercadoPagoCheckoutProRequest: paymentMethodRequest,
        });
      }
    }
  };

  const handleFormSubmit = data => {
    const saveMercadoPagoRequest: MercadoPagoCheckoutProRequest = {
      enabled: true,
      serverParameters: {
        publicKey: data.publicKey.trim(),
        accessToken: data.accessToken.trim(),
      },
      displayName:
        paymentMethod && paymentMethod.displayName === data.displayName
          ? undefined
          : data.displayName,
      clientParameters:
        paymentMethod &&
        paymentMethod.clientParameters &&
        paymentMethod.clientParameters.description === data.description
          ? undefined
          : {description: data.description},
    };
    updateMercadoPagoCheckoutPro({
      mercadoPagoCheckoutProRequest: saveMercadoPagoRequest,
    });
  };

  useEffect(() => {
    if (!MERCADO_PAGO_SUPPORTED_CURRENCY_CODES.includes(currency?.code)) {
      setShowWarningCurrency(true);
      setDisabledButton(true);
    }
  }, [currency]);

  // const [alertOpen, setAlertOpen] = useState(false);

  // useEffect(() => {
  //   if (savingSuccess) setAlertOpen(true);
  //   else setAlertOpen(false);
  //   const timer = setTimeout(() => {
  //     setAlertOpen(false);
  //   }, 2000);
  //   return () => clearTimeout(timer);
  // }, [savingSuccess]);

  // useEffect(() => {
  //   return () => {
  //     resetSavedSuccess();
  //   };
  // }, [resetSavedSuccess]);

  return (
    <div className={classes.root}>
      <Snackbar
        open={showWarningCurrency}
        autoHideDuration={8000}
        onClose={() => setShowWarningCurrency(false)}
      >
        <Alert severity="warning" onClose={() => setShowWarningCurrency(false)}>
          La moneda configurada en tu tienda no es compatible con Mercado Pago.
        </Alert>
      </Snackbar>
      <CustomExitPrompt when={isDirty} />
      {/* <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
      >
        <Alert
          // open
          // className={classes.saveAlert}
          severity="success"
          onClose={() => setAlertOpen(false)}
          // action={
          //   <IconButton
          //     aria-label="close"
          //     color="inherit"
          //     size="small"
          //     onClick={() => {
          //       setAlertOpen(false);
          //     }}
          //   >
          //     <Close fontSize="inherit" />
          //   </IconButton>
          // }
        >
          Configuración guardada
        </Alert>
      </Snackbar> */}
      <div className={classes.subRouteHeader}>
        <IconButton
          variant="contained"
          size="small"
          component={Link}
          to={`/configure/payment-methods${window.location.search}`}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          className={classes.subRouteTitle}
          id="mercadopago-configuration"
        >
          Mercado Pago <NewFeatureBadge />
        </Typography>
      </div>
      <Divider className={classes.divider} />
      {typeof disableActivation === 'boolean' && (
        <FormControlLabel
          classes={{label: classes.checkboxLabel}}
          className={classes.checkbox}
          control={
            <Checkbox
              checked={enabled}
              onChange={handleToggleEnabled}
              color="primary"
              disabled={isSaving || (enabled === false && disableActivation)}
            />
          }
          label={
            disableActivation
              ? 'Debes configurar tus propias tarifas de envío para habilitar esta opción'
              : 'Activar'
          }
        />
      )}
      {enabled && (
        <>
          <Typography className={classes.descriptionText}>
            Debes colocar aquí las llaves de Mercado pago. Si no tienes una
            cuenta, seguir los{' '}
            <a
              href="https://www.mercadopago.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              siguientes pasos
            </a>
          </Typography>
          <div className={classes.row}>
            <CustomTextField
              className={classes.textField}
              label="Public Key"
              name="publicKey"
              defaultValue={
                paymentMethod && paymentMethod.serverParameters
                  ? paymentMethod.serverParameters.public_key
                  : ''
              }
              error={!!errors.publicKey}
              helperText={errors.publicKey ? errors.publicKey.message : ''}
              inputRef={register({
                required: 'Campo obligatorio',
              })}
              type="text"
              disabled={
                isSaving || !hasMercadopagoCheckoutProIntegrationFeature
              }
            />
            <CustomTextField
              className={classes.textField}
              label="Access Token"
              name="accessToken"
              defaultValue={
                paymentMethod && paymentMethod.serverParameters
                  ? paymentMethod.serverParameters.access_token
                  : ''
              }
              error={!!errors.accessToken}
              helperText={errors.accessToken ? errors.accessToken.message : ''}
              inputRef={register({
                required: 'Campo obligatorio',
              })}
              type="text"
              disabled={
                isSaving || !hasMercadopagoCheckoutProIntegrationFeature
              }
            />
          </div>
          <CustomTextField
            className={classes.textField}
            label="Nombre del método de pago que verá tu cliente"
            name="displayName"
            defaultValue={
              paymentMethod
                ? paymentMethod.displayName
                : 'Tarjeta de crédito/débito'
            }
            error={!!errors.displayName}
            helperText={errors.displayName ? errors.displayName.message : ''}
            inputRef={register({
              required: 'Campo obligatorio',
            })}
            type="text"
            disabled={isSaving || !hasMercadopagoCheckoutProIntegrationFeature}
          />
          <CustomTextField
            className={classes.textField}
            multiline
            label="Instrucciones (Opcional)"
            name="description"
            error={!!errors.description}
            helperText={errors.description ? errors.description : ''}
            defaultValue={
              paymentMethod && paymentMethod.clientParameters
                ? paymentMethod.clientParameters.description
                : ''
            }
            type="text"
            inputRef={register}
            rows={4}
            disabled={isSaving || !hasMercadopagoCheckoutProIntegrationFeature}
          />
          {hasMercadopagoCheckoutProIntegrationFeature && (
            <CustomButton
              variant="contained"
              color="primary"
              disabled={disabledButton}
              className={classes.saveButton}
              onClick={handleSubmit(handleFormSubmit)}
            >
              Guardar
            </CustomButton>
          )}
        </>
      )}
    </div>
  );
}

MercadoPagoConfig.propTypes = {
  isDeleting: PropTypes.bool,
  isFetching: PropTypes.bool,
  isSaving: PropTypes.bool,
  error: PropTypes.string,
  paymentMethod: PropTypes.object,
  paymentMethods: PropTypes.array,
  addSuccess: PropTypes.bool,
  deleteSuccess: PropTypes.bool,
  updateSuccess: PropTypes.bool,
  // addPaymentMethod: PropTypes.func.isRequired,
  updateMercadoPagoCheckoutPro: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  getPaymentMethod: PropTypes.func.isRequired,
  store: PropTypes.object,
  currency: PropTypes.object,
  disableActivation: PropTypes.bool,
};
